/** Which models an assembly is used on — the two linked grids.
 *
 * FKMT's GHERP item master carries these side by side: Project Family on the
 * left, and the models of the ticked families on the right. Reproduced here
 * because it is how the people who build these machines already think, and
 * because it is what makes a BOM picker useful — building an MT/SL 6 should
 * offer MT/SL 6 parts, not four thousand items.
 *
 * THREE RULES, and each is enforced rather than encouraged:
 *
 *  1. **Assemblies only.** A plate belongs to no product line; it goes
 *     wherever it is needed. The panel does not appear for a plain Item, and
 *     `mst_item_family`'s foreign key refuses the row even if something else
 *     tried to write it. ITEM_MASTER.md §10.2.
 *
 *  2. **The model is compulsory.** FKMT's decision of 7 Sep 2026, chosen over
 *     the looser option for the precision it buys. The cost is paid here, in
 *     the screen: a part used on six models is six rows, ticked in one pass,
 *     the way GHERP's Equipment grid works.
 *
 *  3. **Ticking a family does not tag anything.** It reveals that family's
 *     models. Only a ticked MODEL is a tag — otherwise a half-finished thought
 *     would be saved as a fact.
 */

import { useEffect, useState } from 'react'
import { api, ApiError } from '@/lib/api'

interface Family { family_code: string; name: string }
interface Model { model_code: string; family_code: string; name: string }
export interface Tag { family_code: string; model_code: string }

const TAGGABLE = ['SubAssembly', 'Assembly', 'MainAssembly']

export function ItemFamilyPanel(
  { itemCategory, tags, onChange, readOnly = false }: {
    itemCategory: string
    tags: Tag[]
    onChange: (tags: Tag[]) => void
    /** No `masters.family.modify`: the tags are shown, not offered for
     *  change. Ticks are disabled here rather than hidden because they ARE
     *  the information - an item's families. RIGHTS_MODEL.md 6.1. */
    readOnly?: boolean
  },
) {
  const [families, setFamilies] = useState<Family[]>([])
  const [models, setModels] = useState<Model[]>([])
  const [openFamilies, setOpenFamilies] = useState<string[]>([])
  const [problem, setProblem] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const taggable = TAGGABLE.includes(itemCategory)

  useEffect(() => {
    if (!taggable) { setLoading(false); return }
    let live = true
    Promise.all([
      api.get<{ rows?: Family[] }>('/masters/product-families'),
      api.get<{ rows?: Model[] }>('/masters/product-models'),
    ])
      .then(([f, m]) => {
        if (!live) return
        setFamilies(f.rows ?? [])
        setModels(m.rows ?? [])
      })
      // An empty list looks like "there is nothing to choose", when the real
      // answer is usually "you cannot see that list yet". Say which.
      .catch((e: unknown) => {
        if (live) setProblem(e instanceof ApiError ? e.message
                                                   : 'could not be loaded')
      })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [taggable])

  /* A family is open if it is ticked OR if it already holds a tag — so
     re-opening a saved item shows its models without anybody hunting. */
  const tagged = new Set(tags.map(t => t.family_code))
  const shown = families.filter(
    f => tagged.has(f.family_code) || openFamilies.includes(f.family_code))

  function toggleFamily(code: string) {
    if (openFamilies.includes(code) || tagged.has(code)) {
      // Closing a family drops its tags, and that is deliberate: leaving
      // hidden ticks behind is how a part ends up on a model nobody meant.
      setOpenFamilies(openFamilies.filter(c => c !== code))
      onChange(tags.filter(t => t.family_code !== code))
    } else {
      setOpenFamilies([...openFamilies, code])
    }
  }

  function toggleModel(m: Model) {
    const on = tags.some(t => t.model_code === m.model_code)
    onChange(on
      ? tags.filter(t => t.model_code !== m.model_code)
      : [...tags, { family_code: m.family_code, model_code: m.model_code }])
  }

  if (!taggable) {
    return (
      <div className="panel" data-testid="family-panel-hidden">
        <h3 className="panel-title">Used On</h3>
        <p className="form-note">
          Only a Sub-Assembly, Assembly or Main Assembly belongs to a product
          family. A plain item goes wherever it is needed, and is found through
          the bill of material.
        </p>
      </div>
    )
  }

  return (
    <div className="panel" data-testid="family-panel">
      <h3 className="panel-title">Used On</h3>

      {loading && <p className="form-note">Loading…</p>}

      {problem && (
        <p className="form-note form-note-warn">
          The product families {problem}.
        </p>
      )}

      {!loading && !problem && families.length === 0 && (
        <p className="form-note">
          No product families yet. Create one under Design ▸ Product Families,
          then its models.
        </p>
      )}

      {readOnly && taggable && !loading && (
        <p className="form-note">
          You can see this item’s product families but not change them.
        </p>
      )}

      {!loading && families.length > 0 && (
        <div className="panel-grids">
          <div className="panel-grid">
            <div className="panel-grid-head">Product Family</div>
            {families.map(f => (
              <label key={f.family_code} className="panel-row">
                <input
                  type="checkbox"
                  checked={tagged.has(f.family_code)
                           || openFamilies.includes(f.family_code)}
                  disabled={readOnly}
                  onChange={() => toggleFamily(f.family_code)}
                />
                <span>{f.name}</span>
              </label>
            ))}
          </div>

          <div className="panel-grid">
            <div className="panel-grid-head">Model</div>
            {shown.length === 0 && (
              <p className="panel-empty">Tick a family to see its models.</p>
            )}
            {shown.map(f => {
              const own = models.filter(m => m.family_code === f.family_code)
              return (
                <div key={f.family_code}>
                  <div className="panel-subhead">{f.name}</div>
                  {own.length === 0 && (
                    <p className="panel-empty">
                      {f.name} has no models yet.
                    </p>
                  )}
                  {own.map(m => (
                    <label key={m.model_code} className="panel-row">
                      <input
                        type="checkbox"
                        checked={tags.some(t => t.model_code === m.model_code)}
                        disabled={readOnly}
                        onChange={() => toggleModel(m)}
                      />
                      <span>{m.name}</span>
                    </label>
                  ))}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {tags.length > 0 && (
        <p className="form-note">
          Tagged to {tags.length} model{tags.length === 1 ? '' : 's'}.
        </p>
      )}
    </div>
  )
}
